/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import React from 'react';
import { decorate, computed, observable, runInAction } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Icon, Popup } from 'semantic-ui-react';
import TimeAgo from 'react-timeago';
import { niceNumber, swallowError } from '@amzn/base-ui/dist/helpers/utils';
import { displaySuccess } from '@amzn/base-ui/dist/helpers/notification';
import { isStoreLoading, isStoreNotEmpty, isStoreError } from '@amzn/base-ui/dist/models/BaseStore';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';
import ProgressPlaceHolder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';

import {
  isAppStreamEnabled,
  enableWorkspaceScheduler,
  schedulerIdleGraceMultiplier,
  schedulerTimezone,
} from '../../helpers/settings';
import { projectedStopFrom, formatProjectedStopWithZone } from '../../helpers/schedule-display';
import { formatConfigName, parseEnvTypeName } from '../../helpers/env-type-display';
import { getRegionDisplayText } from '../../models/constants/aws-regions';
import By from '../helpers/By';
import DeprecatedBadge, { DEPRECATED_TOOLTIP } from '../helpers/DeprecatedBadge';
import ScheduledBadge, { SCHEDULED_LABEL } from '../helpers/ScheduledBadge';
import Expiry from '../helpers/Expiry';
import ScEnvironmentButtons from './parts/ScEnvironmentButtons';
import ScEnvironmentCost from './parts/ScEnvironmentCost';
import StorageUsageDisplay from './parts/StorageUsageDisplay';

// Icon style fix for alignment (matches BudgetCards pattern)
const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

/**
 * Parse hardware info from config description
 * Expected format: "1 CPU (2 threads) / 2 GiB Memory *DCV not supported..."
 */
function parseHardwareInfo(desc) {
  if (!desc) return { cpu: null, memory: null };
  
  const cpuMatch = desc.match(/(\d+)\s*(?:v?CPU|cpu)/i);
  const threadsMatch = desc.match(/\((\d+)\s*threads?\)/i);
  const memoryMatch = desc.match(/(\d+(?:\.\d+)?)\s*(?:GiB|GB|Gib)\s*(?:Memory|RAM)?/i);
  
  let cpuDisplay = null;
  if (cpuMatch) {
    cpuDisplay = cpuMatch[1];
    if (threadsMatch) {
      cpuDisplay += ` (${threadsMatch[1]}t)`;
    }
    cpuDisplay += ' vCPU';
  }
  
  let memoryDisplay = null;
  if (memoryMatch) {
    memoryDisplay = `${memoryMatch[1]} GiB`;
  }
  
  return { cpu: cpuDisplay, memory: memoryDisplay };
}

// expected props
// - scEnvironment (via prop)
// - compactMode (via prop, optional)
// - envTypesStore (via injection)
class ScEnvironmentCard extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.isHovered = false;
    });
  }

  get envTypesStore() {
    return this.props.envTypesStore;
  }

  get environment() {
    return this.props.scEnvironment;
  }

  get envType() {
    const env = this.props.scEnvironment;
    const store = this.envTypesStore;
    return store.getEnvType(env.envTypeId);
  }

  getEnvTypeConfigsStore() {
    return this.envTypesStore.getEnvTypeConfigsStore(this.environment.envTypeId);
  }

  getConfiguration(envTypeConfigId) {
    const configsStore = this.getEnvTypeConfigsStore();
    return configsStore.getEnvTypeConfig(envTypeConfigId);
  }

  componentDidMount() {
    const configsStore = this.getEnvTypeConfigsStore();
    swallowError(configsStore.load());
  }

  handleMouseEnter = () => runInAction(() => { this.isHovered = true; });
  handleMouseLeave = () => runInAction(() => { this.isHovered = false; });

  render() {
    const configsStore = this.getEnvTypeConfigsStore();

    if (isStoreError(configsStore)) {
      return <ErrorBox error={configsStore.error} className="p0" />;
    }
    if (isStoreLoading(configsStore)) {
      return <ProgressPlaceHolder segmentCount={3} />;
    }
    if (isStoreNotEmpty(configsStore)) {
      return this.renderMain();
    }
    return null;
  }

  renderMain() {
    const env = this.environment;
    const state = env.state;
    const config = this.getConfiguration(this.environment.envTypeConfigId);
    const envType = this.envType;
    const instanceType = config ? config.instanceType.split('/')[0] : env.envTypeId;
    const studyIds = _.get(env, 'studyIds', []);
    const studyCount = _.size(studyIds);
    const compactMode = this.props.compactMode;
    
    const hardwareInfo = parseHardwareInfo(config?.desc);
    const softwareInfo = parseEnvTypeName(envType?.name);
    const configName = formatConfigName(config?.name || env.envTypeConfigId);

    // Use hex colors from state object
    const hexColor = state.hexColor || '#6b7280';
    const hexBg = state.hexBg || '#f3f4f6';

    // The meta row's single idle item reports the stop that happened rather than the timeout that is
    // configured, but only for a workspace that is stopped now: on a running one the timeout is the
    // live fact and the earlier stop is history. The phrase is null when the scheduler recorded no
    // readable timestamp, which leaves the configuration item in place.
    const showIdleStopEvent = env.status === 'STOPPED' && !_.isNil(env.idleStopEventPhrase);

    if (compactMode) {
      return this.renderCompact(env, state, config, instanceType, hexColor, hexBg, softwareInfo);
    }

    return (
      <div
        className="workspace-card"
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        <div className="workspace-card-accent" style={{ background: hexColor }} />
        
        <div className="workspace-card-content">
          {/* Header Row */}
          <div className="workspace-card-header">
            <div style={{ flex: 1 }}>
              <h3 className="workspace-card-title">{env.name}</h3>
              <div className="workspace-card-description">
                {env.description || 'No description provided'}
              </div>
            </div>
            <div className="workspace-card-header-badges">
              {softwareInfo.deprecated && <DeprecatedBadge />}
              {/* A timetable being set is a fact about the workspace, so the pill is neutral and sits
                  left of the status badge, which is the fact a reader looks for first. */}
              {enableWorkspaceScheduler && !_.isNil(env.schedulePhrase) && (
                <ScheduledBadge detail={env.schedulePhrase} />
              )}
              <Popup
                trigger={
                  <span 
                    className="workspace-status-badge"
                    style={{ background: hexBg, color: hexColor }}
                  >
                    <Icon name={state.spinner ? 'spinner' : 'circle'} loading={state.spinner} style={iconStyle} />
                    {state.display}
                  </span>
                }
                content={state.tip}
                size="small"
                position="top center"
                inverted
              />
            </div>
          </div>

          {/* Alerts */}
          {this.renderAlerts(env)}

          {/* Info Grid */}
          <div className="workspace-info-grid">
            <InfoCard 
              icon="microchip" 
              label="Hardware"
              items={[
                { label: 'Instance', value: instanceType },
                { label: 'CPU', value: hardwareInfo.cpu || 'N/A' },
                { label: 'Memory', value: hardwareInfo.memory || 'N/A' },
              ]}
            />
            
            <InfoCard 
              icon="code" 
              label="Software"
              items={[
                {
                  label: 'Config',
                  value: configName.display,
                  badge: configName.code,
                  tooltip: configName.truncated ? configName.full : undefined,
                },
                { label: 'OS', value: softwareInfo.os },
                { label: 'Software', value: softwareInfo.software },
              ]}
            />
            
            <StorageCard 
              rootVolume={env.rootVolume || null}
              diskUtilizationGB={env.status !== 'TERMINATED' ? env.diskUtilizationGB : null}
              studyCount={studyCount}
              studyIds={studyIds}
            />
            
            <div className="workspace-info-card">
              <ScEnvironmentCost envId={env.id} compact cpuUtilization={env.status !== 'TERMINATED' ? env.cpuUtilization : null} cpuUtilizationUpdatedAt={env.cpuUtilizationUpdatedAt} />
            </div>
          </div>

          {/* Meta info row */}
          <div className="workspace-meta-row">
            <span className="workspace-meta-item">
              <Icon name="clock outline" style={iconStyle} />
              Created <TimeAgo date={env.createdAt} />
            </span>
            <span className="workspace-meta-item">
              <Icon name="user outline" style={iconStyle} />
              <By uid={env.createdBy} />
            </span>
            {env.status !== "EXPIRED" && env.status !== "TERMINATED" && (
              <span className="workspace-meta-item">
                <Icon name="hourglass half" style={iconStyle} />
                <Expiry date={env.expiresAt} />
              </span>
            )}

            {/* One idle item, next to the other facts about the workspace rather than in a tile of its
                own: it is one short phrase, the same shape as Created, the owner and Expiry. The
                timetable is not here, it is a pill beside the status badge, because its phrase can run
                to a week of differing hours and this row is a single line. An outstanding idle warning
                is not here either, it is an alert above. With the feature flag off nothing about
                scheduling renders at all (Requirement 1.7).

                Which idle fact depends on the status. For a stopped workspace the scheduler has
                recorded an idle stop against, the item says that stop happened and when: "why is this
                stopped?" is the question a user has on finding it stopped, and its future timeout is
                not an answer to it. Otherwise the item carries the configuration. Either way it is one
                item, so the row does not grow. */}
            {enableWorkspaceScheduler && showIdleStopEvent && (
              <span className="workspace-meta-item" data-testid="sc-env-idle-stop-event-meta">
                <Icon name="moon outline" style={iconStyle} />
                {env.idleStopEventPhrase} <TimeAgo date={env.idleStopStoppedAt} />
              </span>
            )}
            {enableWorkspaceScheduler && !showIdleStopEvent && !_.isNil(env.idleStopPhrase) && (
              <span className="workspace-meta-item" data-testid="sc-env-idle-stop-meta">
                <Icon name="moon outline" style={iconStyle} />
                {env.idleStopPhrase}
              </span>
            )}

            <span className="workspace-meta-item">
              <Icon name="briefcase" style={iconStyle} />
              {_.isEmpty(env.projectId) ? 'No project' : env.projectId}
            </span>
            {!_.isEmpty(env.displayRegion) && (
              <span className="workspace-meta-item">
                <Icon name="globe" style={iconStyle} />
                {getRegionDisplayText(env.displayRegion)}
              </span>
            )}
            <span className="workspace-meta-item">
              <Icon name="hashtag" style={iconStyle} />
              {env.id}
              <Icon
                name="copy outline"
                style={{ ...iconStyle, cursor: 'pointer', color: '#9ca3af', marginLeft: '2px', fontSize: '0.85em', verticalAlign: 'middle' }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(env.id).then(() => displaySuccess('Workspace ID copied', 'Copied!')).catch(() => {});
                }}
                title="Copy workspace ID"
              />
            </span>
          </div>

          {/* Action Buttons */}
          <ScEnvironmentButtons scEnvironment={env} config={config} showDetailButton />
        </div>
      </div>
    );
  }

  renderCompact(env, state, config, instanceType, hexColor, hexBg, softwareInfo) {
    const alertIcons = this.getAlertIcons(env);

    // The neutral marker says a timeout is configured. While a warning is outstanding the amber alert
    // icon says the same thing and adds when the workspace will stop, so the neutral one is suppressed
    // rather than sitting a few pixels away restating the weaker half of it.
    const showIdleStopMarker = !_.isNil(env.idleStopPhrase) && !env.hasIdleStopWarning;

    return (
      <div
        className="workspace-card workspace-card-compact"
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        <div className="workspace-card-accent" style={{ background: hexColor }} />
        
        <div className="workspace-card-content">
          {/* Compact Header */}
          <div className="workspace-card-header-compact">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3 className="workspace-card-title-compact">{env.name}</h3>
                {alertIcons.length > 0 && (
                  <div className="workspace-compact-alerts">
                    {alertIcons}
                  </div>
                )}
              </div>
              <div className="workspace-card-subtitle-compact">
                {softwareInfo.os} • {instanceType}
              </div>
            </div>
            {/* The neutral markers and the status badge share one right-hand group so that a 1em icon
                is centred against the badge rather than against the two-line title block: the header
                itself aligns its children to the top, which the title needs and the icons do not. */}
            <div className="workspace-card-header-compact-right">
              {/* Either scheduling feature being set is a fact about the workspace, not a problem with
                  it, so both sit beside the status badge in a muted group rather than joining the alert
                  icons above. */}
              {enableWorkspaceScheduler && (!_.isNil(env.schedulePhrase) || showIdleStopMarker) && (
                <div className="workspace-compact-schedule">
                  {!_.isNil(env.schedulePhrase) && (
                    <Popup
                      trigger={
                        <Icon
                          name="calendar alternate outline"
                          className="workspace-compact-schedule-icon"
                          style={iconStyle}
                          data-testid="sc-env-compact-schedule-icon"
                        />
                      }
                      // The pill on the full card carries the word in its label; here there is no
                      // label, so the tooltip says it.
                      content={`${SCHEDULED_LABEL}: ${env.schedulePhrase}`}
                      size="small"
                      position="top center"
                      inverted
                    />
                  )}
                  {showIdleStopMarker && (
                    <Popup
                      trigger={
                        <Icon
                          name="clock outline"
                          className="workspace-compact-schedule-icon"
                          style={iconStyle}
                          data-testid="sc-env-compact-idle-stop-icon"
                        />
                      }
                      content={env.idleStopPhrase}
                      size="small"
                      position="top center"
                      inverted
                    />
                  )}
                </div>
              )}
              <Popup
                trigger={
                  <span
                    className="workspace-status-badge workspace-status-badge-compact"
                    style={{ background: hexBg, color: hexColor }}
                  >
                    <Icon name={state.spinner ? 'spinner' : 'circle'} loading={state.spinner} style={iconStyle} />
                    {state.display}
                  </span>
                }
                content={state.tip}
                size="small"
                position="top center"
                inverted
              />
            </div>
          </div>

          {/* Compact Action Buttons */}
          <ScEnvironmentButtons scEnvironment={env} config={config} showDetailButton compactMode />
        </div>
      </div>
    );
  }

  // When the external scheduler will stop a workspace it has judged idle, as a phrase, or null when
  // that cannot be worked out.
  //
  // The list renders from the tag mirror and makes no call per row, so there is no server-computed
  // `projectedStopAt` here: the time is derived from the warning timestamp, the timeout and the
  // deployment's grace multiplier, by the same helper the arithmetic lives in. Null whenever any of
  // those is missing or unreadable, and callers then say the workspace will stop soon rather than
  // inventing a time. A wrong time here is worse than a vague one, because the user would plan around it.
  getIdleStopTime(env) {
    return formatProjectedStopWithZone(
      projectedStopFrom(env.idleStopWarnedAt, env.idleStopMinutes, schedulerIdleGraceMultiplier),
      schedulerTimezone,
    );
  }

  getAlertIcons(env) {
    const icons = [];

    // Error message
    if (!_.isEmpty(env.error)) {
      icons.push(
        <Popup
          key="error"
          trigger={<Icon name="exclamation circle" className="workspace-compact-alert-icon workspace-compact-alert-error" style={iconStyle} />}
          content={<><strong>Error:</strong> {env.error}</>}
          size="small"
          position="top center"
          inverted
        />
      );
    }

    // COMPLETED_EXPIRED status
    if (env.status === 'COMPLETED_EXPIRED') {
      icons.push(
        <Popup
          key="completed-expired"
          trigger={<Icon name="clock" className="workspace-compact-alert-icon workspace-compact-alert-error" style={iconStyle} />}
          content={<><strong>Auto-Stop Imminent:</strong> This workspace has expired and will automatically stop soon.</>}
          size="small"
          position="top center"
          inverted
        />
      );
    }

    // Outstanding idle warning. An alert rather than a neutral marker: the workspace is about to stop
    // and there is something the user can do about it. The schedule itself is a separate, muted icon
    // beside the status badge, because a timetable is not an alert.
    if (enableWorkspaceScheduler && env.hasIdleStopWarning) {
      const stopAt = this.getIdleStopTime(env);

      icons.push(
        <Popup
          key="idle-stop-warning"
          trigger={
            <Icon
              name="moon"
              className="workspace-compact-alert-icon workspace-compact-alert-warning"
              style={iconStyle}
              data-testid="sc-env-idle-stop-alert-icon"
            />
          }
          content={
            <>
              <strong>Idle Stop Pending:</strong> This workspace has been judged idle and will stop{' '}
              {stopAt ? `at ${stopAt}` : 'soon'}.
            </>
          }
          size="small"
          position="top center"
          inverted
        />,
      );
    }

    // Expiry warning
    if (env.expiresAt && env.status !== 'EXPIRED' && env.status !== 'TERMINATED' && env.status !== 'COMPLETED_EXPIRED') {
      const expiryDate = new Date(env.expiresAt);
      const now = new Date();
      const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysRemaining <= 14 && daysRemaining > 0) {
        icons.push(
          <Popup
            key="expiry-warning"
            trigger={<Icon name="hourglass half" className="workspace-compact-alert-icon workspace-compact-alert-warning" style={iconStyle} />}
            content={<><strong>Expiring Soon:</strong> {daysRemaining} day{daysRemaining === 1 ? '' : 's'} remaining</>}
            size="small"
            position="top center"
            inverted
          />
        );
      }
    }

    // AppStream warning
    if (isAppStreamEnabled && !env.isAppStreamConfigured && env.state.canTerminate) {
      icons.push(
        <Popup
          key="appstream"
          trigger={<Icon name="warning sign" className="workspace-compact-alert-icon workspace-compact-alert-warning" style={iconStyle} />}
          content={<><strong>Non-AppStream:</strong> Please terminate this workspace.</>}
          size="small"
          position="top center"
          inverted
        />
      );
    }

    // Legacy RStudio warning
    const metaConnection1Type = env.outputs.find(obj => obj.OutputKey === 'MetaConnection1Type');
    if (metaConnection1Type && metaConnection1Type.OutputValue.toLowerCase() === 'rstudio' && env.state.canTerminate) {
      icons.push(
        <Popup
          key="rstudio"
          trigger={<Icon name="warning sign" className="workspace-compact-alert-icon workspace-compact-alert-warning" style={iconStyle} />}
          content={<><strong>Legacy RStudio:</strong> Please use RStudioV2 instead.</>}
          size="small"
          position="top center"
          inverted
        />
      );
    }

    // Deprecated workspace type. Icon rather than the full badge, since the compact row has no
    // space for a text pill; the tooltip text is shared with DeprecatedBadge.
    if (parseEnvTypeName(this.envType?.name).deprecated) {
      icons.push(
        <Popup
          key="deprecated"
          trigger={<Icon name="history" className="workspace-compact-alert-icon workspace-compact-alert-warning" style={iconStyle} />}
          content={<><strong>Deprecated:</strong> {DEPRECATED_TOOLTIP}</>}
          size="small"
          position="top center"
          inverted
        />
      );
    }

    return icons;
  }

  renderAlerts(env) {
    const alerts = [];

    // Error message
    if (!_.isEmpty(env.error)) {
      alerts.push(
        <Alert key="error" type="error" title="Error" message={env.error} />
      );
    }

    // Capacity error -- shown when last start attempt failed due to insufficient capacity
    if (env.capacityError) {
      alerts.push(
        <Alert
          key="capacity"
          type="error"
          title="Capacity Unavailable"
          message="The last launch attempt failed due to insufficient capacity. Try launching again, or creating a workspace in another region."
        />
      );
    }

    // COMPLETED_EXPIRED status - workspace will auto-stop soon
    if (env.status === 'COMPLETED_EXPIRED') {
      alerts.push(
        <Alert 
          key="completed-expired" 
          type="error" 
          title="Auto-Stop Imminent" 
          message="This workspace has expired and will automatically stop soon. Please save your work immediately."
        />
      );
    }

    // Outstanding idle warning. Above the expiry warning, because expiry is days away and this is
    // minutes or hours away. With the feature flag off nothing about scheduling appears anywhere on
    // this card (Requirement 1.7).
    if (enableWorkspaceScheduler && env.hasIdleStopWarning) {
      const stopAt = this.getIdleStopTime(env);

      alerts.push(
        <Alert
          key="idle-stop-warning"
          type="warning"
          title="Idle Stop Pending"
          message={
            stopAt
              ? `This workspace has been judged idle and will stop at ${stopAt}. Please save your work.`
              : 'This workspace has been judged idle and will stop soon. Please save your work.'
          }
        />,
      );
    }

    // Expiry warning - 14 days or less remaining
    if (env.expiresAt && env.status !== 'EXPIRED' && env.status !== 'TERMINATED' && env.status !== 'COMPLETED_EXPIRED') {
      const expiryDate = new Date(env.expiresAt);
      const now = new Date();
      const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysRemaining <= 14 && daysRemaining > 0) {
        alerts.push(
          <Alert 
            key="expiry-warning" 
            type="warning" 
            title="Expiring Soon" 
            message={`This workspace expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}. Please migrate your data to a new workspace as soon as possible.`}
          />
        );
      }
    }

    // AppStream warning
    if (isAppStreamEnabled && !env.isAppStreamConfigured && env.state.canTerminate) {
      alerts.push(
        <Alert 
          key="appstream" 
          type="warning" 
          title="Non-AppStream Workspace" 
          message="Please terminate this workspace. It is not supported by AppStream."
        />
      );
    }

    // Legacy RStudio warning
    const metaConnection1Type = env.outputs.find(obj => obj.OutputKey === 'MetaConnection1Type');
    if (metaConnection1Type && metaConnection1Type.OutputValue.toLowerCase() === 'rstudio' && env.state.canTerminate) {
      alerts.push(
        <Alert 
          key="rstudio" 
          type="warning" 
          title="Legacy RStudio" 
          message="This environment type is deprecated. Please use RStudioV2 instead."
          link={{
            href: "https://github.com/awslabs/service-workbench-on-aws/blob/mainline/docs/Service_Workbench_Post_Deployment_Guide.pdf",
            text: "Learn more"
          }}
        />
      );
    }

    if (alerts.length === 0) return null;

    return <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>{alerts}</div>;
  }
}

// Each item is { label, value } and may optionally carry:
// - badge: short text rendered in a coloured square to the left of the label
// - tooltip: full text revealed on hover, for values shortened to fit the tile
function InfoCard({ icon, label, items }) {
  return (
    <div className="workspace-info-card">
      <div className="workspace-info-card-label">
        <Icon name={icon} style={iconStyle} />
        {label}
      </div>
      <div>
        {items.map((item, idx) => (
          <div key={idx} className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">{item.label}</span>
            {item.badge ? (
              <span className="workspace-info-card-row-value-group">
                <span className="workspace-info-card-row-badge">{item.badge}</span>
                <InfoCardValue value={item.value} tooltip={item.tooltip} />
              </span>
            ) : (
              <InfoCardValue value={item.value} tooltip={item.tooltip} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// No title attribute on the trigger: it renders the browser's own tooltip on top of the Popup.
function InfoCardValue({ value, tooltip }) {
  if (!tooltip) return <span className="workspace-info-card-row-value">{value}</span>;

  return (
    <Popup
      trigger={<span className="workspace-info-card-row-value">{value}</span>}
      content={tooltip}
      size="small"
      position="top center"
      inverted
    />
  );
}

function StorageCard({ rootVolume, diskUtilizationGB, studyCount, studyIds }) {
  // Calculate warning state for positioning icon in top-right corner
  let isWarning = false;
  if (rootVolume !== null) {
    const total = parseFloat(rootVolume);
    const used = parseFloat(diskUtilizationGB);
    if (_.isFinite(total) && total > 0 && _.isFinite(used) && diskUtilizationGB !== null && diskUtilizationGB !== undefined) {
      const percentage = Math.min((used / total) * 100, 100);
      isWarning = percentage > 80;
    }
  }

  const storageContent = (
    <div className="workspace-info-card" style={{ cursor: studyCount > 0 ? 'pointer' : 'default', position: 'relative' }}>
      {/* Warning icon in top-right corner */}
      {isWarning && (
        <Popup
          trigger={
            <Icon 
              name="warning sign" 
              style={{ 
                ...iconStyle, 
                color: '#dc2626', 
                position: 'absolute',
                top: '12px',
                right: '12px',
              }} 
              data-testid="storage-warning-icon"
            />
          }
          content="Storage usage is above 80%. Consider freeing up space or expanding storage."
          size="small"
          position="top right"
        />
      )}
      <div className="workspace-info-card-label">
        <Icon name="hdd" style={iconStyle} />
        Storage
      </div>
      <div>
        <div className="workspace-info-card-row">
          <span className="workspace-info-card-row-label">Root Volume</span>
          <span className="workspace-info-card-row-value">
            {rootVolume !== null ? (
              <StorageUsageDisplay totalGB={rootVolume} usedGB={diskUtilizationGB} hideWarningIcon />
            ) : (
              'N/A'
            )}
          </span>
        </div>
        <div className="workspace-info-card-row">
          <span className="workspace-info-card-row-label">Studies</span>
          <span 
            className="workspace-info-card-row-value"
            style={{ 
              color: studyCount > 0 ? '#2563eb' : '#111827',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {studyCount === 0 ? 'None' : niceNumber(studyCount)}
            {studyCount > 0 && <Icon name="info circle" style={{ ...iconStyle, color: '#9ca3af' }} />}
          </span>
        </div>
      </div>
    </div>
  );

  if (studyCount > 0) {
    return (
      <Popup trigger={storageContent} position="top center" size="small" hoverable>
        <div style={{ maxWidth: '250px' }}>
          <div style={{ fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
            Mounted Studies ({studyCount})
          </div>
          <div style={{ fontSize: '0.9em', color: '#6b7280' }}>
            {studyIds.map((id, idx) => (
              <div key={idx} style={{ 
                padding: '4px 0', 
                borderBottom: idx < studyIds.length - 1 ? '1px solid #f3f4f6' : 'none' 
              }}>
                {id}
              </div>
            ))}
          </div>
        </div>
      </Popup>
    );
  }

  return storageContent;
}

function Alert({ type, title, message, link }) {
  const className = type === 'error' ? 'workspace-alert workspace-alert-error' : 'workspace-alert workspace-alert-warning';
  const iconName = type === 'error' ? 'exclamation circle' : 'warning sign';

  return (
    <div className={className}>
      <Icon name={iconName} style={iconStyle} />
      <div>
        <strong>{title}:</strong> {message}
        {link && (
          <a 
            href={link.href} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ marginLeft: '6px', textDecoration: 'underline' }}
          >
            {link.text}
          </a>
        )}
      </div>
    </div>
  );
}

decorate(ScEnvironmentCard, {
  envTypesStore: computed,
  environment: computed,
  envType: computed,
  config: observable,
  isHovered: observable,
});

export default inject('envTypesStore')(withRouter(observer(ScEnvironmentCard)));
